import React, { useState } from 'react';

/**
 * VitalsFieldGroup - Renders vital signs with optional pre/post measurements
 * Shows pre and post values on the same row with timestamps for easy comparison
 */
const VitalsFieldGroup = ({ formData, onChange, errors, readOnly }) => {
  const measurementType = formData.vitals_measurement_type || 'Single measurement';
  const isPrePost = measurementType === 'Pre/Post measurements';
  const [showNotes, setShowNotes] = useState(Boolean(formData.vitals_notes));

  // Static vitals that don't change during a session
  const staticVitals = [
    { id: 'vitals_height', label: 'Height', unit: 'inches', placeholder: 'e.g., 68', type: 'number' },
    { id: 'vitals_weight', label: 'Weight', unit: 'lbs', placeholder: 'e.g., 150', type: 'number' },
    { id: 'vitals_temperature', label: 'Temperature', unit: 'F', placeholder: 'e.g., 98.6', type: 'text' },
  ];

  // Dynamic vitals that may need pre/post measurements
  const dynamicVitals = [
    { id: 'vitals_blood_pressure', postId: 'vitals_post_blood_pressure', label: 'Blood Pressure', placeholder: 'e.g., 120/80', type: 'text' },
    { id: 'vitals_heart_rate', postId: 'vitals_post_heart_rate', label: 'Heart Rate', unit: 'bpm', placeholder: 'e.g., 72', type: 'number' },
    { id: 'vitals_respiratory_rate', postId: 'vitals_post_respiratory_rate', label: 'Respiratory Rate', unit: 'breaths/min', placeholder: 'e.g., 16', type: 'number' },
    { id: 'vitals_oxygen_saturation', postId: 'vitals_post_oxygen_saturation', label: 'O2 Saturation', unit: '%', placeholder: 'e.g., 98', type: 'text' },
  ];

  const handleChange = (fieldId, value) => {
    if (readOnly) return;
    onChange(fieldId, value);
  };

  const renderInput = (id, placeholder, type = 'text', className = '') => (
    <input
      type={type}
      value={formData[id] || ''}
      onChange={(e) => handleChange(id, e.target.value)}
      placeholder={placeholder}
      disabled={readOnly}
      className={`border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 ${className}`}
    />
  );

  const renderTimeInput = (id) => (
    <input
      type="time"
      value={formData[id] || ''}
      onChange={(e) => handleChange(id, e.target.value)}
      disabled={readOnly}
      className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 w-24"
    />
  );

  return (
    <div className="border border-gray-300 rounded-lg p-2 mb-2">
      <div className="flex justify-between items-center mb-1">
        <h3 className="text-base font-semibold">Vital Signs</h3>
      </div>

      {/* Measurement Type Dropdown */}
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <label className="text-sm font-medium text-gray-700">
          Measurement Type
        </label>
        <select
          value={measurementType}
          onChange={(e) => handleChange('vitals_measurement_type', e.target.value)}
          disabled={readOnly}
          className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
        >
          <option value="Single measurement">Single measurement</option>
          <option value="Pre/Post measurements">Pre/Post measurements</option>
        </select>
        <p className="text-xs text-gray-500">
          Select &apos;Pre/Post&apos; for medication administration or injection sessions
        </p>
      </div>

      {/* Static Vitals - Single row each */}
      <div className="mb-2">
        <div className="grid grid-cols-2 gap-2 md:grid-cols-3 xl:grid-cols-6">
          {staticVitals.map((vital) => (
            <div key={vital.id}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {vital.label} {vital.unit && <span className="text-gray-500">({vital.unit})</span>}
              </label>
              {renderInput(vital.id, vital.placeholder, vital.type, 'w-full')}
              {errors[vital.id] && (
                <p className="text-red-500 text-xs mt-1">{errors[vital.id]}</p>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Dynamic Vitals - Pre/Post on same row when applicable */}
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        {dynamicVitals.map((vital) => (
          <div key={vital.id} className="rounded border border-gray-200 p-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {vital.label} {vital.unit && <span className="text-gray-500">({vital.unit})</span>}
            </label>
            
            {isPrePost ? (
              /* Pre/Post Mode - Show both on same row */
              <div className="grid grid-cols-1 gap-2 xl:grid-cols-2">
                {/* Pre measurement */}
                <div className="flex items-center gap-2 bg-blue-50 p-2 rounded">
                  <span className="text-sm font-medium text-blue-700 w-10">Pre:</span>
                  {renderInput(vital.id, vital.placeholder, vital.type, 'flex-1')}
                  <span className="text-sm text-gray-500">at</span>
                  {renderTimeInput(`${vital.id}_time`)}
                </div>
                
                {/* Post measurement */}
                <div className="flex items-center gap-2 bg-green-50 p-2 rounded">
                  <span className="text-sm font-medium text-green-700 w-10">Post:</span>
                  {renderInput(vital.postId, vital.placeholder, vital.type, 'flex-1')}
                  <span className="text-sm text-gray-500">at</span>
                  {renderTimeInput(`${vital.postId}_time`)}
                </div>
              </div>
            ) : (
              /* Single Mode - Just one input */
              <div className="flex items-center gap-2">
                {renderInput(vital.id, vital.placeholder, vital.type, 'w-48')}
              </div>
            )}
            
            {(errors[vital.id] || errors[vital.postId]) && (
              <p className="text-red-500 text-xs mt-1">
                {errors[vital.id] || errors[vital.postId]}
              </p>
            )}
          </div>
        ))}
      </div>

      {/* Vitals Notes */}
      <div className="mt-2 border-t border-gray-200 pt-2">
        <button
          type="button"
          onClick={() => setShowNotes((value) => !value)}
          className="flex w-full items-center justify-between text-left text-sm font-medium text-gray-700"
          aria-expanded={showNotes}
        >
          <span>Vitals Notes</span>
          <span aria-hidden="true">{showNotes ? '−' : '+'}</span>
        </button>
        {showNotes && (
        <textarea
          value={formData.vitals_notes || ''}
          onChange={(e) => handleChange('vitals_notes', e.target.value)}
          placeholder="Any additional notes about vital signs..."
          disabled={readOnly}
          rows={2}
          className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
        />
        )}
      </div>
    </div>
  );
};

export default VitalsFieldGroup;
